import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { UserCog, Edit2, Trash2, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label } from '@caruso/ui';

type ClientField =
  | 'name'
  | 'phone'
  | 'cep'
  | 'cpf'
  | 'email'
  | 'birthDate'
  | 'address'
  | 'addressNumber'
  | 'addressComplement'
  | 'neighborhood'
  | 'city'
  | 'state';

interface FieldUpdate {
  id: string;
  field: ClientField;
  variable: string;
}

interface UpdateClientNodeData {
  updates?: FieldUpdate[];
}

const fieldLabels: Record<ClientField, string> = {
  name: 'Nome',
  phone: 'Telefone',
  cep: 'CEP',
  cpf: 'CPF',
  email: 'Email',
  birthDate: 'Data de Nascimento',
  address: 'Endereço',
  addressNumber: 'Número',
  addressComplement: 'Complemento',
  neighborhood: 'Bairro',
  city: 'Cidade',
  state: 'Estado',
};

export const UpdateClientNode = memo(({ data, id }: NodeProps<UpdateClientNodeData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [updates, setUpdates] = useState<FieldUpdate[]>(data.updates || []);

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              updates,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const addUpdate = () => {
    const newUpdate: FieldUpdate = {
      id: `update-${Date.now()}`,
      field: 'name',
      variable: '',
    };
    setUpdates([...updates, newUpdate]);
  };

  const removeUpdate = (updateId: string) => {
    setUpdates(updates.filter((u) => u.id !== updateId));
  };

  const updateField = (updateId: string, field: keyof FieldUpdate, value: string) => {
    setUpdates(
      updates.map((u) => (u.id === updateId ? { ...u, [field]: value } : u))
    );
  };

  return (
    <>
      <div className="bg-purple-500/10 border-2 border-purple-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-purple-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
              <UserCog className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-purple-600">Atualizar Cliente</div>
              <div className="text-xs text-gray-500">Atualiza dados do cliente</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-purple-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-purple-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {updates.length > 0 ? (
            <div className="text-xs text-gray-600">
              {updates.length} atualização{updates.length !== 1 ? 'ões' : ''} configurada{updates.length !== 1 ? 's' : ''}
            </div>
          ) : (
            <div className="text-xs text-purple-600 italic">
              ⚠️ Configure as atualizações
            </div>
          )}

          {updates.length > 0 && (
            <div className="text-xs text-gray-500 space-y-1">
              {updates.slice(0, 3).map((update) => (
                <div key={update.id} className="truncate">
                  • {fieldLabels[update.field]}
                </div>
              ))}
              {updates.length > 3 && (
                <div className="text-gray-400">
                  + {updates.length - 3} mais...
                </div>
              )}
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-purple-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory min-w-[765px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Configurar Atualização de Cliente
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure quais campos do cliente serão atualizados com variáveis do fluxo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-brand-ivory/80">
                  Atualizações <span className="text-red-400">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addUpdate}
                  className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Atualização
                </Button>
              </div>

              {updates.length === 0 ? (
                <div className="text-center p-8 bg-brand-gold/5 rounded border border-brand-gold/20 text-brand-ivory/60">
                  Nenhuma atualização configurada. Clique em "Adicionar Atualização" para começar.
                </div>
              ) : (
                <div className="space-y-3">
                  {updates.map((update, index) => (
                    <div
                      key={update.id}
                      className="p-4 bg-brand-gold/5 rounded border border-brand-gold/20"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-brand-ivory/80">
                          Atualização #{index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUpdate(update.id)}
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Campo do Cliente
                          </Label>
                          <select
                            value={update.field}
                            onChange={(e) =>
                              updateField(update.id, 'field', e.target.value)
                            }
                            className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
                          >
                            {Object.entries(fieldLabels).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label className="text-brand-ivory/70 text-xs">
                            Variável do Fluxo
                          </Label>
                          <Input
                            value={update.variable}
                            onChange={(e) =>
                              updateField(update.id, 'variable', e.target.value)
                            }
                            placeholder="Ex: {{nome_completo}}"
                            className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 bg-brand-gold/5 rounded border border-brand-gold/20">
              <p className="text-sm text-brand-ivory/80 mb-2 font-medium">
                ℹ️ Como funciona:
              </p>
              <ul className="text-xs text-brand-ivory/60 space-y-1 list-disc list-inside">
                <li>Use variáveis capturadas anteriormente no fluxo (ex: <code className="bg-brand-ink/50 px-1 rounded">{'{{nome_completo}}'}</code>)</li>
                <li>As variáveis podem vir de respostas de mensagens, formulários ou nós anteriores</li>
                <li>O cliente será atualizado automaticamente quando o fluxo passar por este nó</li>
                <li>Se a variável estiver vazia, o campo não será atualizado</li>
                <li>Múltiplos campos podem ser atualizados de uma só vez</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={updates.length === 0 || updates.some((u) => !u.variable)}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Salvar Configuração
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

UpdateClientNode.displayName = 'UpdateClientNode';
